import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Sparkles, Send, Rocket, MousePointerClick, X,
  ExternalLink, Copy, Check, RefreshCw, MessageSquare, Globe2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import { toast } from "sonner";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle,
} from "@/components/ui/drawer";

const EDIT_SCRIPT = `
<script>
(function(){
  document.addEventListener('click', function(e){
    var a = e.target.closest('a');
    if(a){ e.preventDefault(); }
    if(!window.__hoEdit) return;
    e.preventDefault(); e.stopPropagation();
    var el = e.target;
    var info = { tag: el.tagName.toLowerCase(), text: (el.innerText||'').trim().slice(0,140), html: el.outerHTML.slice(0,400) };
    parent.postMessage({ type:'ho-element-tap', info: info }, '*');
    var prev = el.style.outline;
    el.style.outline='2px solid #0055FF'; el.style.outlineOffset='2px';
    setTimeout(function(){ el.style.outline=prev; }, 1400);
  }, true);
  window.addEventListener('message', function(ev){
    if(ev.data && ev.data.type==='ho-set-edit'){ window.__hoEdit = ev.data.on; document.body.style.cursor = ev.data.on ? 'crosshair' : ''; }
  });
})();
<\/script>`;

function buildDoc(html) {
  if (!html) return `<!doctype html><html><body style="font-family:sans-serif;display:grid;place-items:center;height:100vh;color:#999">Your preview will appear here</body></html>`;
  if (html.includes("</body>")) return html.replace("</body>", `${EDIT_SCRIPT}</body>`);
  return html + EDIT_SCRIPT;
}

export default function Builder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const scrollRef = useRef(null);

  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [elementCtx, setElementCtx] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [domainOpen, setDomainOpen] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [domainBusy, setDomainBusy] = useState(false);
  const [domainInfo, setDomainInfo] = useState(null);

  const { user } = useAuth();
  const busy = !!project?.generating;

  const connectDomain = async () => {
    const d = domainInput.trim();
    if (!d) return;
    setDomainBusy(true);
    try {
      const { data } = await client.post(`/projects/${id}/domain`, { domain: d });
      setDomainInfo(data);
      setProject((p) => ({ ...p, custom_domain: data.domain }));
      toast.success("Domain attached! Add the DNS records below.");
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(err.response.data.detail);
        setDomainOpen(false);
        setTimeout(() => navigate("/pricing"), 1200);
      } else {
        toast.error(err?.response?.data?.detail || "Couldn't connect domain");
      }
    } finally {
      setDomainBusy(false);
    }
  };

  const loadAll = useCallback(async () => {
    try {
      const [{ data: p }, { data: m }] = await Promise.all([
        client.get(`/projects/${id}`),
        client.get(`/projects/${id}/messages`),
      ]);
      setProject(p);
      setMessages(m);
    } catch {
      toast.error("Couldn't load this project");
      navigate("/app");
    }
  }, [id, navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const handler = (ev) => {
      if (ev.data?.type === "ho-element-tap") {
        setElementCtx(ev.data.info);
        setChatOpen(true);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage({ type: "ho-set-edit", on: editMode }, "*");
  }, [editMode, project?.html]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, chatOpen]);

  // Poll while the AI is generating/editing
  useEffect(() => {
    if (!project?.generating) return;
    const iv = setInterval(async () => {
      try {
        const { data } = await client.get(`/projects/${id}`);
        setProject(data);
        if (!data.generating) {
          const { data: m } = await client.get(`/projects/${id}/messages`);
          setMessages(m);
          clearInterval(iv);
        }
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(iv);
  }, [project?.generating, id]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    const ctxNote = elementCtx
      ? { role: "user", content: `✎ ${elementCtx.text ? `"${elementCtx.text}"` : elementCtx.tag}: ${text}` }
      : { role: "user", content: text };
    setMessages((prev) => [...prev, { ...ctxNote, id: `tmp-${Date.now()}` }]);
    try {
      const { data } = await client.post(`/projects/${id}/generate`, {
        prompt: text,
        element_context: elementCtx ? `<${elementCtx.tag}> containing "${elementCtx.text}". Markup: ${elementCtx.html}` : undefined,
      });
      setProject(data);
      setElementCtx(null);
      setEditMode(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Edit failed. Try again.");
    }
  };

  const deploy = async () => {
    setDeploying(true);
    try {
      const { data } = await client.post(`/projects/${id}/deploy`);
      setProject((p) => ({ ...p, status: "live", live_url: data.live_url }));
      setDeployResult(data);
      toast.success("Your site is live!");
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(err.response.data.detail || "Upgrade to publish more sites");
        setTimeout(() => navigate("/pricing"), 1200);
      } else {
        toast.error(err?.response?.data?.detail || "Deploy failed");
      }
    } finally {
      setDeploying(false);
    }
  };

  const copyLink = async () => {
    if (!project?.live_url) return;
    await navigator.clipboard.writeText(project.live_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!project) {
    return (
      <div className="grid min-h-screen place-items-center bg-ho-bg">
        <div className="w-9 h-9 rounded-full border-[3px] border-ho-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-ho-bg">
      {/* Top bar */}
      <header className="z-30 flex items-center gap-2 border-b border-black/5 bg-white/90 px-3 py-2.5 backdrop-blur-xl">
        <button onClick={() => navigate("/app")} data-testid="builder-back"
          className="tap-scale grid place-items-center w-9 h-9 rounded-full bg-black/5">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-[15px] font-bold leading-tight">{project.name}</div>
          <div className="flex items-center gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${project.status === "live" ? "bg-emerald-500" : "bg-amber-500"}`} />
            <span className="text-[11px] font-semibold text-ho-muted">{project.status === "live" ? "Live" : "Draft"}</span>
          </div>
        </div>
        {project.status === "live" && (
          <button onClick={() => setDomainOpen(true)} data-testid="builder-domain"
            className="tap-scale grid place-items-center w-9 h-9 rounded-full bg-black/5">
            <Globe2 className="w-5 h-5" />
          </button>
        )}
        <button onClick={deploy} disabled={deploying} data-testid="builder-deploy"
          className="tap-scale inline-flex items-center gap-1.5 rounded-full bg-ho-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
          {deploying ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
          {deploying ? "Deploying" : project.status === "live" ? "Redeploy" : "Deploy"}
        </button>
      </header>

      {/* Edit-mode banner */}
      <AnimatePresence>
        {editMode && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="z-20 bg-ho-primary text-white">
            <div className="flex items-center justify-between gap-2 px-4 py-2 text-sm font-medium">
              <span className="flex items-center gap-2"><MousePointerClick className="w-4 h-4" /> Tap any part of your site to edit it</span>
              <button onClick={() => setEditMode(false)} data-testid="exit-edit-mode" className="tap-scale rounded-full bg-white/20 px-3 py-1 text-xs font-semibold">Done</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preview */}
      <div className="relative flex-1 overflow-hidden">
        <iframe
          ref={iframeRef}
          title="preview"
          data-testid="builder-preview"
          srcDoc={buildDoc(project.html)}
          className="h-full w-full border-0 bg-white"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
        {busy && (
          <div className="absolute inset-0 grid place-items-center bg-white/70 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3">
              <div className="grid place-items-center w-14 h-14 rounded-2xl bg-ho-primary/10">
                <Sparkles className="w-7 h-7 text-ho-primary animate-pulse" />
              </div>
              <p className="text-sm font-semibold text-ho-ink">{project.html ? "Applying your changes…" : "Building your site…"}</p>
            </div>
          </div>
        )}
      </div>

      {/* Floating controls */}
      <div className="pointer-events-none absolute bottom-5 left-0 right-0 z-30 flex justify-center gap-3 px-5">
        <button onClick={() => setEditMode((v) => !v)} data-testid="toggle-edit-mode"
          className={`tap-scale pointer-events-auto inline-flex items-center gap-2 rounded-full px-5 py-3.5 font-semibold shadow-lg ${editMode ? "bg-ho-ink text-white" : "bg-white text-ho-ink border border-black/5"}`}>
          <MousePointerClick className="w-5 h-5" /> Tap-edit
        </button>
        <button onClick={() => setChatOpen(true)} data-testid="open-chat"
          className="tap-scale pointer-events-auto inline-flex items-center gap-2 rounded-full bg-ho-primary px-5 py-3.5 font-semibold text-white shadow-[0_10px_30px_rgba(0,85,255,0.4)]">
          <Sparkles className="w-5 h-5" /> Ask AI
        </button>
      </div>

      {/* Chat Drawer */}
      <Drawer open={chatOpen} onOpenChange={setChatOpen}>
        <DrawerContent className="h-[78vh]" data-testid="chat-drawer">
          <DrawerHeader className="border-b border-black/5">
            <DrawerTitle className="flex items-center gap-2 font-display">
              <MessageSquare className="w-5 h-5 text-ho-primary" /> Edit with AI
            </DrawerTitle>
          </DrawerHeader>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4 no-scrollbar">
            {messages.length === 0 && (
              <p className="mt-6 text-center text-sm text-ho-muted">Describe any change — "make the hero darker", "add a pricing section", "change the title to Bloom Studio".</p>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[15px] ${m.role === "user" ? "bg-ho-primary text-white" : "bg-black/5 text-ho-ink"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="rounded-2xl bg-black/5 px-4 py-3">
                  <div className="flex gap-1">
                    {[0, 1, 2].map((i) => <span key={i} className="w-2 h-2 rounded-full bg-ho-muted animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />)}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-black/5 px-4 pb-6 pt-3">
            <AnimatePresence>
              {elementCtx && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="mb-2 flex items-center justify-between gap-2 rounded-xl bg-ho-primary/10 px-3 py-2">
                  <span className="truncate text-xs font-medium text-ho-primary">
                    Editing: {elementCtx.text ? `"${elementCtx.text}"` : `<${elementCtx.tag}>`}
                  </span>
                  <button onClick={() => setElementCtx(null)} data-testid="clear-element-ctx" className="shrink-0">
                    <X className="w-4 h-4 text-ho-primary" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={1}
                placeholder="Describe a change…"
                data-testid="chat-input"
                className="max-h-32 flex-1 resize-none rounded-2xl border border-black/10 bg-ho-bg px-4 py-3 text-[15px] outline-none focus:border-ho-primary"
              />
              <button onClick={send} disabled={!input.trim() || busy} data-testid="chat-send"
                className="tap-scale grid place-items-center w-12 h-12 shrink-0 rounded-2xl bg-ho-primary text-white disabled:opacity-50">
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Deploy success drawer */}
      <Drawer open={!!deployResult} onOpenChange={(o) => !o && setDeployResult(null)}>
        <DrawerContent data-testid="deploy-success-sheet">
          <div className="px-5 pb-8 pt-4 text-center">
            <div className="mx-auto grid place-items-center w-16 h-16 rounded-3xl bg-emerald-100">
              <Rocket className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="font-display mt-4 text-2xl font-bold">You're live! 🎉</h3>
            <p className="mt-1 text-ho-muted">Your site is published at a real URL.</p>
            <div className="mt-5 flex items-center gap-2 rounded-2xl border border-black/10 bg-ho-bg px-4 py-3">
              <span className="truncate text-left text-sm font-medium text-ho-primary">{project.live_url}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <button onClick={copyLink} data-testid="deploy-copy"
                className="tap-scale inline-flex items-center justify-center gap-2 rounded-full bg-black/5 py-3.5 font-semibold">
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy link"}
              </button>
              <a href={project.live_url} target="_blank" rel="noreferrer" data-testid="deploy-open"
                className="tap-scale inline-flex items-center justify-center gap-2 rounded-full bg-ho-primary py-3.5 font-semibold text-white">
                Open <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Custom domain drawer */}
      <Drawer open={domainOpen} onOpenChange={(o) => { setDomainOpen(o); if (!o) setDomainInfo(null); }}>
        <DrawerContent className="max-h-[85vh]" data-testid="domain-sheet">
          <DrawerHeader>
            <DrawerTitle className="flex items-center gap-2 font-display">
              <Globe2 className="w-5 h-5 text-ho-primary" /> Custom domain
            </DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto px-5 pb-8 no-scrollbar">
            {(user?.plan || "free") === "free" ? (
              <div className="rounded-3xl border border-dashed border-black/15 bg-white p-6 text-center">
                <div className="mx-auto grid place-items-center w-14 h-14 rounded-2xl bg-ho-primary/10">
                  <Sparkles className="w-7 h-7 text-ho-primary" />
                </div>
                <h4 className="font-display mt-4 text-lg font-bold">Custom domains are a Pro feature</h4>
                <p className="mt-2 text-sm text-ho-muted">Upgrade to connect your own domain like <span className="font-semibold">yoursite.com</span>.</p>
                <button onClick={() => navigate("/pricing")} data-testid="domain-upgrade"
                  className="tap-scale mt-5 w-full rounded-full bg-ho-primary py-3.5 font-semibold text-white">
                  See Pro plans
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm text-ho-muted">
                  Point your own domain (e.g. <span className="font-semibold text-ho-ink">yoursite.com</span>) to this published site instead of the netlify.app URL.
                </p>
                <div className="mt-4 flex gap-2">
                  <input
                    value={domainInput}
                    onChange={(e) => setDomainInput(e.target.value)}
                    placeholder="yoursite.com"
                    data-testid="domain-input"
                    className="h-12 flex-1 rounded-2xl border border-black/10 bg-ho-bg px-4 text-[15px] outline-none focus:border-ho-primary"
                  />
                  <button onClick={connectDomain} disabled={domainBusy || !domainInput.trim()} data-testid="domain-connect"
                    className="tap-scale inline-flex items-center gap-1.5 rounded-2xl bg-ho-primary px-5 font-semibold text-white disabled:opacity-50">
                    {domainBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Connect"}
                  </button>
                </div>
                {project.custom_domain && !domainInfo && (
                  <div className="mt-3 rounded-2xl bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    Connected: <span className="font-semibold">{project.custom_domain}</span>
                  </div>
                )}
                {domainInfo && (
                  <div className="mt-5">
                    <div className="text-sm font-bold">Add these DNS records at your registrar</div>
                    <div className="mt-3 space-y-2">
                      {domainInfo.records.map((rec, i) => (
                        <div key={i} className="rounded-2xl border border-black/10 bg-ho-bg p-3 font-mono text-xs">
                          <Rec label="Type" value={rec.type} />
                          <Rec label="Host" value={rec.host} />
                          <Rec label="Value" value={rec.value} />
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 text-xs text-ho-muted">
                      DNS changes can take a few minutes to a few hours. SSL is issued automatically once your domain resolves.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

const Rec = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 py-0.5">
    <span className="text-ho-muted">{label}</span>
    <span className="truncate font-semibold text-ho-ink">{value}</span>
  </div>
);
